import { IPrinter } from '@/interfaces/IPrinter';
// Type-only on purpose: use-printing imports this module, so a value import
// would be circular at runtime.
import type {
  IPrintingStrategy,
  IPrintRawBytesParams,
  usePrintingStore,
} from '../use-printing';
import { PrintPluginStatus } from '@/modules/PrintService';
import { ref, watch } from 'vue';
import { vueData } from '@/VueData';
import { AlertType } from '@/interfaces/IAlert';

/**
 * Legacy Android fallback: old APKs (Capacitor 5 era) bundle the
 * `thermal-printer-cordova-plugin` natively and its JS global is injected by
 * the shell itself, so this strategy needs NO npm dependency — only the
 * minimal typed surface below. It keeps those devices printing exactly as
 * before until they install the new APK (which ships the EscPosPrinter
 * plugin and switches them to printing-strategy-native).
 *
 * Behavior is a 1:1 port of the pre-V2 native strategy (master), adapted to
 * the current IPrintingStrategy surface (printFormattedText is gone: every
 * document is rendered to raw bytes upstream).
 */

interface ILegacyThermalPrinterDevice {
  deviceId: number | string;
  name?: string;
  productName?: string;
  manufacturerName?: string;
}

interface ILegacyRequestPermissionsResult {
  granted?: boolean;
}

interface ILegacyThermalPrinterPlugin {
  listPrinters(
    data: { type: 'usb' | 'bluetooth' },
    success: (printers: ILegacyThermalPrinterDevice[]) => void,
    error: (err: unknown) => void
  ): void;
  printRawBytes(
    data: {
      id: number | string;
      type: 'usb' | 'bluetooth' | 'tcp';
      /** The legacy plugin expects the payload base64-encoded in `bytes`. */
      bytes: string;
    },
    success: () => void,
    error: (err: unknown) => void
  ): void;
  requestPermissions(
    data: { id: number | string; type: 'usb' },
    success: (res: ILegacyRequestPermissionsResult) => void,
    error: (err: unknown) => void
  ): void;
}

declare global {
  interface Window {
    /** Injected natively by old APKs' cordova bridge; absent everywhere else. */
    ThermalPrinter?: ILegacyThermalPrinterPlugin;
  }
}

/**
 * The subset of the printing store the permission watchers read. Accepting
 * the narrower structural type keeps the strategy honest about what it uses
 * (and remains assignable to IPrintingStrategy['setStore'] by parameter
 * contravariance).
 */
export type TPrinterAssignments = Pick<
  ReturnType<typeof usePrintingStore>,
  'ticketPrinter' | 'stickerPrinter' | 'comandaPrintersByStation'
>;

/** IPrintingStrategy with setStore narrowed to the fields actually read. */
export interface ILegacyNativeStrategy
  extends Omit<IPrintingStrategy, 'setStore'> {
  setStore: (store: TPrinterAssignments) => void;
}

/**
 * Lazy lookup: the strategy is only ever selected on legacy Android shells
 * (where the shell injects the global before app code runs), but a missing
 * plugin must fail a single print call with a clear error, never crash the
 * whole store at module init.
 */
function getPlugin(): ILegacyThermalPrinterPlugin {
  const plugin = window.ThermalPrinter;
  if (!plugin) {
    throw new Error('El plugin de impresión nativo no está disponible');
  }
  return plugin;
}

export function printingStrategyNativeLegacy(): ILegacyNativeStrategy {
  const status = ref<PrintPluginStatus>(PrintPluginStatus.Disconnected);

  async function requestPermissions(printer: IPrinter) {
    return new Promise<ILegacyRequestPermissionsResult>((resolve, reject) => {
      getPlugin().requestPermissions(
        {
          id: printer.id,
          type: 'usb',
        },
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  function setStore(store: TPrinterAssignments) {
    async function requestPermissionsSafe(printer: IPrinter) {
      try {
        await requestPermissions(printer);
      } catch (e) {
        vueData.addAlert(
          'Error obteniendo permisos para imprimir de ' + printer.name,
          AlertType.Error
        );
        console.error(e);
      }
    }

    const printerKeys = ['ticketPrinter', 'stickerPrinter'] as const;

    for (const key of printerKeys) {
      watch(
        () => store[key],
        async (printer, old) => {
          if (printer && printer.id !== old?.id) {
            await requestPermissionsSafe(printer);
          }
        },
        { immediate: true }
      );
    }

    // Comanda printers are keyed by print station: request permissions for
    // each newly assigned printer.
    watch(
      () => store.comandaPrintersByStation,
      async (byStation, oldByStation) => {
        for (const key of Object.keys(byStation ?? {})) {
          const printer = byStation[+key];
          const old = oldByStation?.[+key];

          if (printer && printer.id !== old?.id) {
            await requestPermissionsSafe(printer);
          }
        }
      },
      { immediate: true, deep: true }
    );
  }

  async function getPrinters(mode: 'usb' | 'bluetooth') {
    return new Promise<IPrinter[]>((resolve, reject) => {
      getPlugin().listPrinters(
        { type: mode },
        function (printers) {
          resolve(
            printers.map((p) => ({
              id: p.deviceId,
              // Same precedence the pre-V2 strategy persisted names with —
              // it must not change, or saved assignments would stop
              // resolving. The deviceId tail only covers nameless devices.
              name:
                p.name ||
                p.productName ||
                p.manufacturerName ||
                String(p.deviceId),
              type: mode,
            }))
          );
        },
        function (error) {
          reject(error);
        }
      );
    });
  }

  async function printRawBytes(data: IPrintRawBytesParams): Promise<void> {
    let id = data.id;
    let type = data.type;

    if (!id) {
      if (!data.printer) {
        throw new Error(
          'If no printer id is provided, a printer object must be provided'
        );
      }

      id = data.printer.id;
      type = data.printer.type;
    }

    return new Promise<void>((resolve, reject) => {
      getPlugin().printRawBytes(
        {
          id,
          type: type ?? 'usb',
          bytes: data.base64,
        },
        function () {
          resolve();
        },
        function (error) {
          vueData.addAlert(
            `Error imprimiendo en ${data.printer?.name ?? id}`,
            AlertType.Error
          );
          reject(error);
        }
      );
    });
  }

  return {
    getPrinters,
    printRawBytes,
    status,
    setStore,
    connect: () => {
      status.value = PrintPluginStatus.Connected;
    },
  };
}
